import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { getAlertHistory } from '../../api/alerts';
import { getUserProtocolRisk } from '../../api/protocol-risk';
import { listComplianceRuns } from '../../api/compliance-reports';
import { getUserYieldOpportunities } from '../../api/yield-opportunities';
import { getUserSignals, countNonNeutralSignals } from '../../api/ai-signals';
import { getSubscriptions } from '../../api/portfolio-subscriptions';
import apiClient from '../../api/client';
import type { Portfolio } from '../../types';
import { useCurrencyDisplay } from '../../hooks/useCurrencyDisplay';

const SIDEBAR_KEY = 'sidebar-collapsed';

const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10.5L10 3l7 7.5" /><path d="M5 9v7a1 1 0 001 1h3v-4h2v4h3a1 1 0 001-1V9" />
  </svg>
);

const BarChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="10" width="4" height="8" rx="1" /><rect x="8" y="6" width="4" height="12" rx="1" /><rect x="14" y="2" width="4" height="16" rx="1" />
  </svg>
);

const GoalsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="7" />
    <circle cx="10" cy="10" r="3" />
    <path d="M10 1.5v2M10 16.5v2M1.5 10h2M16.5 10h2" />
  </svg>
);

const WalletIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="16" height="12" rx="2" /><path d="M2 9h16" /><circle cx="14" cy="13" r="1" fill="currentColor" />
  </svg>
);

const ListIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 5h11M6 10h11M6 15h11" /><circle cx="3" cy="5" r="1" fill="currentColor" /><circle cx="3" cy="10" r="1" fill="currentColor" /><circle cx="3" cy="15" r="1" fill="currentColor" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 14l5-5 4 4 7-8" /><path d="M13 5h5v5" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2l7 3v5c0 4-3 6.5-7 8-4-1.5-7-4-7-8V5l7-3z" />
  </svg>
);

const LayersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2L2 7l8 5 8-5-8-5z" /><path d="M2 10l8 5 8-5" /><path d="M2 13l8 5 8-5" />
  </svg>
);

const ProtocolRiskIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2l8 3.75V10c0 4.5-3 8-8 9.5C5 18 2 14.5 2 10V5.75L10 2z" />
    <path d="M10 7v4M10 13h.01" />
  </svg>
);

const ScalesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2v16M4 7l-2 4h4L4 7zM16 7l-2 4h4l-2-4z" /><path d="M3 18h14" /><path d="M7 18h6" />
  </svg>
);

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2a5 5 0 00-5 5v3l-2 3h14l-2-3V7a5 5 0 00-5-5z" /><path d="M8 15a2 2 0 004 0" />
  </svg>
);

const BalanceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2v16M6 6l4-4 4 4" /><path d="M4 14h12" /><path d="M2 18h16" />
  </svg>
);

const DCAClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
    <path d="M5 3l-2 2M19 3l2 2" />
  </svg>
);

const BoltIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 2L4 11h7v7l9-11h-7V2z" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="2.5" />
    <path d="M10 2v1.5M10 16.5V18M2 10h1.5M16.5 10H18M4.22 4.22l1.06 1.06M14.72 14.72l1.06 1.06M4.22 15.78l1.06-1.06M14.72 5.28l1.06-1.06" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2l7 3v5c0 4-3 6.5-7 8-4-1.5-7-4-7-8V5l7-3z" />
    <path d="M7 10l2 2 4-4" />
  </svg>
);

const CollapseIcon = ({ collapsed }: { collapsed: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: collapsed ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s' }}>
    <path d="M12 4l-5 6 5 6" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 2H4a2 2 0 00-2 2v12a2 2 0 002 2h3" /><path d="M14 14l4-4-4-4" /><path d="M18 10H8" />
  </svg>
);

const ImportIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 14v1a2 2 0 002 2h8a2 2 0 002-2v-1" /><path d="M10 3v9" /><path d="M6 8l4 4 4-4" />
  </svg>
);

const LiveViewIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 10c0 0 3-5 8-5s8 5 8 5-3 5-8 5-8-5-8-5z" />
    <circle cx="10" cy="10" r="2" />
    <path d="M10 2v1M10 17v1M2 10H1M19 10h-1" />
  </svg>
);

const BacktestIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 16l4-5 3 3 3-4 4-6" />
    <path d="M2 3v13h15" />
    <circle cx="14" cy="4" r="1.5" fill="currentColor" />
  </svg>
);

const YieldScannerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 3v14M6 7l4-4 4 4" />
    <path d="M4 11h12" />
    <path d="M3 15h14" />
  </svg>
);

const AISignalsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const WhaleTrackerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 10c0-4 2.5-7 8-7 7 0 9 5 9 8 0 5-3 8-9 8-2 0-4-.5-5.5-1.5" />
    <path d="M4 10c-1 1-2 2.5-2 4s1 3 3 3" />
    <circle cx="15" cy="10" r="1.5" fill="currentColor" stroke="none" />
    <path d="M7 14c0 1.5 1 3 3 3" />
  </svg>
);

const CommunityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const ComplianceScheduleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2l6 3v4.5c0 3.5-2.5 5.5-6 6.5-3.5-1-6-3-6-6.5V5l6-3z" />
    <path d="M7.5 10l1.5 1.5 3-3" />
  </svg>
);

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  badgeNode?: React.ReactNode;
}

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

function PortfolioTotalSurface({
  totalUsd,
  status,
  compact = false,
}: {
  totalUsd: number | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  compact?: boolean;
}) {
  const { formatValue } = useCurrencyDisplay();

  if (compact) {
    return (
      <div className="mx-2 mb-3 flex min-h-[44px] items-center justify-center rounded-xl border border-border bg-surface-hover px-2 text-center">
        <span className="text-[11px] font-mono tabular-nums text-text-primary">
          {status === 'loading' ? '…' : totalUsd != null ? formatValue(totalUsd, { compact: true }) : '—'}
        </span>
      </div>
    );
  }

  return (
    <div className="mx-2 mb-3 rounded-xl border border-border bg-surface-hover px-3 py-3">
      <p className="text-[11px] uppercase tracking-[0.12em] text-text-muted">Portfolio total</p>
      {status === 'loading' ? (
        <div className="mt-2 h-7 w-28 animate-pulse rounded bg-surface-active" />
      ) : totalUsd != null ? (
        <p className="mt-2 whitespace-nowrap font-mono tabular-nums text-lg font-semibold text-text-primary">
          {formatValue(totalUsd)}
        </p>
      ) : status === 'error' ? (
        <p className="mt-2 text-xs text-text-muted">Portfolio total unavailable</p>
      ) : (
        <p className="mt-2 text-xs text-text-muted">No portfolio data yet</p>
      )}
    </div>
  );
}

const portfolioNav: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: <HomeIcon /> },
  { to: '/portfolio-analytics', label: 'Portfolio Analytics', icon: <BarChartIcon /> },
  { to: '/goals', label: 'Goals', icon: <GoalsIcon /> },
  { to: '/wallet-hub', label: 'Wallets', icon: <WalletIcon /> },
  { to: '/transactions', label: 'Transactions', icon: <ListIcon /> },
  { to: '/import', label: 'Import Transactions', icon: <ImportIcon /> },
  { to: '/market', label: 'Market', icon: <TrendingUpIcon /> },
  { to: '/live', label: 'Live View', icon: <LiveViewIcon />, badgeNode: <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> },
  { to: '/backtest', label: 'Backtesting', icon: <BacktestIcon /> },
  { to: '/community', label: 'Leaderboard', icon: <CommunityIcon /> },
];

const intelligenceNav: NavItem[] = [
  { to: '/risk-intelligence', label: 'Risk', icon: <ShieldIcon /> },
  { to: '/defi-health', label: 'DeFi Health', icon: <LayersIcon /> },
  { to: '/protocol-risk', label: 'Protocol Risk', icon: <ProtocolRiskIcon /> },
  { to: '/yield-scanner', label: 'Yield Scanner', icon: <YieldScannerIcon /> },
  { to: '/signals', label: 'AI Signals', icon: <AISignalsIcon /> },
  { to: '/whale-tracker', label: 'Whale Tracker', icon: <WhaleTrackerIcon /> },
  { to: '/tax-optimisation', label: 'Tax', icon: <ScalesIcon /> },
  { to: '/alerts', label: 'Alerts', icon: <BellIcon /> },
];

const actionsNav: NavItem[] = [
  { to: '/rebalancing', label: 'Rebalancing', icon: <BalanceIcon /> },
  { to: '/dca', label: 'DCA', icon: <DCAClockIcon /> },
  { to: '/automation', label: 'Automation', icon: <BoltIcon /> },
];

const accountNav: NavItem[] = [
  { to: '/display-settings', label: 'Settings', icon: <SettingsIcon /> },
];

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onMobileClose }) => {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(SIDEBAR_KEY) === 'true';
  });
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [fGradeCount, setFGradeCount] = useState(0);
  const [complianceFailedCount, setComplianceFailedCount] = useState(0);
  const [yieldUpgradeCount, setYieldUpgradeCount] = useState(0);
  const [aiSignalCount, setAiSignalCount] = useState(0);
  const [communityFollowCount, setCommunityFollowCount] = useState(0);
  const [portfolioTotalUsd, setPortfolioTotalUsd] = useState<number | null>(null);
  const [portfolioTotalStatus, setPortfolioTotalStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  const isFirmPlus = user?.tier === 'firm' || user?.tier === 'enterprise';

  // Fetch unacknowledged alert count for badge
  const refreshAlertBadge = useCallback(async () => {
    if (!user?.id) return;
    try {
      const history = await getAlertHistory(user.id, 100);
      setUnacknowledgedCount(history.filter((h) => !h.acknowledged_at).length);
    } catch {
      // silently ignore badge fetch errors
    }
  }, [user?.id]);

  // Fetch F-grade protocol count for badge
  const refreshProtocolRiskBadge = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await getUserProtocolRisk(user.id);
      setFGradeCount(data.profiles.filter((p) => p.risk_grade === 'F').length);
    } catch {
      // silently ignore
    }
  }, [user?.id]);

  // Fetch compliance failed runs in last 7 days for amber badge
  const refreshComplianceBadge = useCallback(async () => {
    if (!user?.id || !isFirmPlus) return;
    try {
      const { runs } = await listComplianceRuns(user.id);
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      const count = runs.filter((r) => {
        return r.status === 'failed' && (Date.now() - new Date(r.created_at).getTime()) < sevenDaysMs;
      }).length;
      setComplianceFailedCount(count);
    } catch {
      // silently ignore
    }
  }, [user?.id, isFirmPlus]);

  // Fetch yield upgrade opportunity count for emerald badge
  const refreshYieldBadge = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await getUserYieldOpportunities(user.id);
      setYieldUpgradeCount(data.upgradeOpportunityCount);
    } catch {
      // silently ignore
    }
  }, [user?.id]);

  // Fetch non-neutral AI signal count for badge
  const refreshAISignalBadge = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await getUserSignals(user.id);
      setAiSignalCount(countNonNeutralSignals(data.signals));
    } catch {
      // silently ignore
    }
  }, [user?.id]);

  // Fetch community follow count for badge (number of portfolios followed)
  const refreshCommunityBadge = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await getSubscriptions();
      setCommunityFollowCount(data.total);
    } catch {
      // silently ignore
    }
  }, [user?.id]);

  useEffect(() => {
    void refreshAlertBadge();
    const interval = setInterval(() => { void refreshAlertBadge(); }, 60_000);
    return () => clearInterval(interval);
  }, [refreshAlertBadge]);

  useEffect(() => {
    void refreshProtocolRiskBadge();
    const interval = setInterval(() => { void refreshProtocolRiskBadge(); }, 5 * 60_000);
    return () => clearInterval(interval);
  }, [refreshProtocolRiskBadge]);

  useEffect(() => {
    void refreshComplianceBadge();
    const interval = setInterval(() => { void refreshComplianceBadge(); }, 5 * 60_000);
    return () => clearInterval(interval);
  }, [refreshComplianceBadge]);

  useEffect(() => {
    void refreshYieldBadge();
    const interval = setInterval(() => { void refreshYieldBadge(); }, 15 * 60_000);
    return () => clearInterval(interval);
  }, [refreshYieldBadge]);

  useEffect(() => {
    void refreshAISignalBadge();
    const interval = setInterval(() => { void refreshAISignalBadge(); }, 30 * 60_000);
    return () => clearInterval(interval);
  }, [refreshAISignalBadge]);

  useEffect(() => {
    void refreshCommunityBadge();
    const interval = setInterval(() => { void refreshCommunityBadge(); }, 5 * 60_000);
    return () => clearInterval(interval);
  }, [refreshCommunityBadge]);

  useEffect(() => {
    let active = true;

    if (!user?.id) {
      setPortfolioTotalUsd(null);
      setPortfolioTotalStatus('idle');
      return () => {
        active = false;
      };
    }

    setPortfolioTotalStatus('loading');

    void apiClient
      .get<Portfolio>(`/analytics/portfolio/${user.id}`)
      .then((response) => {
        if (!active) return;
        const total = typeof response.data?.totalValueUsd === 'number' ? response.data.totalValueUsd : null;
        setPortfolioTotalUsd(total);
        setPortfolioTotalStatus('ready');
      })
      .catch(() => {
        if (!active) return;
        setPortfolioTotalUsd(null);
        setPortfolioTotalStatus('error');
      });

    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, String(collapsed));
  }, [collapsed]);

  const intelligenceNavWithBadge: NavItem[] = intelligenceNav.map((item) => {
    if (item.to === '/alerts') return { ...item, badge: unacknowledgedCount };
    if (item.to === '/protocol-risk' && fGradeCount > 0) return { ...item, badge: fGradeCount };
    if (item.to === '/yield-scanner' && yieldUpgradeCount > 0) {
      return {
        ...item,
        badgeNode: (
          <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-bold leading-none">
            {yieldUpgradeCount}
          </span>
        ),
      };
    }
    if (item.to === '/signals' && aiSignalCount > 0) {
      return {
        ...item,
        badgeNode: (
          <span className="px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-[10px] font-bold leading-none">
            {aiSignalCount}
          </span>
        ),
      };
    }
    return item;
  });

  // Portfolio nav with community follow-count badge
  const portfolioNavWithBadge: NavItem[] = portfolioNav.map((item) => {
    if (item.to === '/community' && communityFollowCount > 0) {
      return {
        ...item,
        badgeNode: (
          <span className="px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-[10px] font-bold leading-none">
            {communityFollowCount}
          </span>
        ),
      };
    }
    return item;
  });

  // Close mobile drawer on route change
  useEffect(() => {
    if (mobileOpen && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleSignOut = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/login');
    window.location.reload();
  };

  const linkClasses = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
      isActive
        ? 'bg-primary-600/15 text-primary-400 border-l-2 border-primary-500 -ml-px'
        : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
    }`;

  // Swipe-to-close for mobile drawer
  const touchStartX = useRef<number>(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    if (dx > 60) onMobileClose?.();
  };

  const NavGroup = ({
    label,
    items,
    onItemClick,
    showLabels,
  }: {
    label: string;
    items: NavItem[];
    onItemClick?: () => void;
    showLabels: boolean;
  }) => (
    <>
      {showLabels && (
        <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
          {label}
        </p>
      )}
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={linkClasses}
          title={!showLabels ? item.label : undefined}
          onClick={onItemClick}
        >
          <span className="flex-shrink-0 relative">
            {item.icon}
            {item.badge != null && item.badge > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
            {item.badgeNode != null && item.badge == null && (
              <span className="absolute -top-0.5 -right-0.5">{item.badgeNode}</span>
            )}
          </span>
          {showLabels && <span className="truncate">{item.label}</span>}
        </NavLink>
      ))}
    </>
  );

  return (
    <>
      {/* Mobile slide-over backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => onMobileClose?.()}
          aria-hidden="true"
        />
      )}

      {/* Mobile slide-over drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 flex flex-col bg-surface border-r border-border transform transition-transform duration-200 md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Mobile navigation"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Logo area */}
        <div className="flex items-center h-14 px-4 border-b border-border">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <img src="/hedgehog-logo.png" alt="Pinecurl logo" className="w-10 h-10 flex-shrink-0 rounded" />
            <span className="flex flex-col leading-tight flex-1 min-w-0">
              <span className="text-lg font-bold tracking-tight text-text-primary">Pinecurl</span>
              <span className="text-[9px] font-semibold bg-purple-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wide w-fit">Beta</span>
            </span>
          </div>
          <button
            onClick={() => onMobileClose?.()}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-hover transition-colors"
            aria-label="Close menu"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4l12 12M16 4L4 16" />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-1">
          <PortfolioTotalSurface totalUsd={portfolioTotalUsd} status={portfolioTotalStatus} />
          <NavGroup label="Portfolio" items={portfolioNavWithBadge} onItemClick={() => onMobileClose?.()} showLabels />
          <div className="my-4 border-t border-border" />
          <NavGroup label="Intelligence" items={intelligenceNavWithBadge} onItemClick={() => onMobileClose?.()} showLabels />
          <div className="my-4 border-t border-border" />
          <NavGroup label="Actions" items={actionsNav} onItemClick={() => onMobileClose?.()} showLabels />
          <div className="my-4 border-t border-border" />
          <NavGroup label="Account" items={accountNav} onItemClick={() => onMobileClose?.()} showLabels />
          {isFirmPlus && (
            <>
              <div className="my-4 border-t border-border" />
              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-muted">Institutional</p>
              <NavLink to="/compliance/reports" className={linkClasses} onClick={() => onMobileClose?.()}>
                <span className="flex-shrink-0 relative">
                  <ComplianceScheduleIcon />
                  {complianceFailedCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                      {complianceFailedCount > 9 ? '9+' : complianceFailedCount}
                    </span>
                  )}
                </span>
                <span className="truncate">Compliance Reports</span>
              </NavLink>
            </>
          )}
          {user?.role === 'admin' && (
            <>
              <div className="my-4 border-t border-border" />
              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-muted">Platform</p>
              <NavLink to="/admin" className={linkClasses} onClick={() => onMobileClose?.()}>
                <span className="flex-shrink-0"><ShieldCheckIcon /></span>
                <span className="truncate">Admin Dashboard</span>
              </NavLink>
            </>
          )}
        </nav>

        {/* User footer */}
        <div className="border-t border-border p-3">
          {user && (
            <div className="mb-2 px-1">
              <p className="text-sm font-medium text-text-primary truncate">
                {user.displayName || user.email}
              </p>
              <p className="text-xs text-text-muted truncate">{user.email}</p>
            </div>
          )}
          <button
            onClick={handleSignOut}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-text-secondary hover:bg-surface-hover hover:text-danger transition-colors"
          >
            <LogoutIcon />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex flex-col h-screen bg-surface border-r border-border sticky top-0 transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Logo area */}
        <div className="flex items-center h-14 px-4 border-b border-border">
          {!collapsed && (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <img src="/hedgehog-logo.png" alt="Pinecurl logo" className="w-10 h-10 flex-shrink-0 rounded" />
              <span className="flex flex-col leading-tight flex-1 min-w-0">
                <span className="text-lg font-bold tracking-tight text-text-primary">Pinecurl</span>
                <span className="text-[9px] font-semibold bg-purple-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wide w-fit">Beta</span>
              </span>
            </div>
          )}
          {collapsed && (
            <img src="/hedgehog-logo.png" alt="Pinecurl" className="w-10 h-10 mx-auto rounded" />
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-hover transition-colors ${
              collapsed ? 'mx-auto' : 'ml-auto'
            }`}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <CollapseIcon collapsed={collapsed} />
          </button>
        </div>

        {/* Primary nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-1">
          {!collapsed ? (
            <PortfolioTotalSurface totalUsd={portfolioTotalUsd} status={portfolioTotalStatus} />
          ) : (
            <PortfolioTotalSurface totalUsd={portfolioTotalUsd} status={portfolioTotalStatus} compact />
          )}
          <NavGroup label="Portfolio" items={portfolioNavWithBadge} showLabels={!collapsed} />
          <div className="my-4 border-t border-border" />
          <NavGroup label="Intelligence" items={intelligenceNavWithBadge} showLabels={!collapsed} />
          <div className="my-4 border-t border-border" />
          <NavGroup label="Actions" items={actionsNav} showLabels={!collapsed} />
          <div className="my-4 border-t border-border" />
          <NavGroup label="Account" items={accountNav} showLabels={!collapsed} />
          {isFirmPlus && (
            <>
              <div className="my-4 border-t border-border" />
              {!collapsed && (
                <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
                  Institutional
                </p>
              )}
              <NavLink
                to="/compliance/reports"
                className={linkClasses}
                title={collapsed ? 'Compliance Reports' : undefined}
              >
                <span className="flex-shrink-0 relative">
                  <ComplianceScheduleIcon />
                  {complianceFailedCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                      {complianceFailedCount > 9 ? '9+' : complianceFailedCount}
                    </span>
                  )}
                </span>
                {!collapsed && <span className="truncate">Compliance Reports</span>}
              </NavLink>
            </>
          )}
          {user?.role === 'admin' && (
            <>
              <div className="my-4 border-t border-border" />
              {!collapsed && (
                <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
                  Platform
                </p>
              )}
              <NavLink
                to="/admin"
                className={linkClasses}
                title={collapsed ? 'Admin Dashboard' : undefined}
              >
                <span className="flex-shrink-0"><ShieldCheckIcon /></span>
                {!collapsed && <span className="truncate">Admin Dashboard</span>}
              </NavLink>
            </>
          )}
        </nav>

        {/* User footer */}
        <div className="border-t border-border p-3">
          {!collapsed && user && (
            <div className="mb-2 px-1">
              <p className="text-sm font-medium text-text-primary truncate">
                {user.displayName || user.email}
              </p>
              <p className="text-xs text-text-muted truncate">{user.email}</p>
            </div>
          )}
          <button
            onClick={handleSignOut}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-text-secondary hover:bg-surface-hover hover:text-danger transition-colors"
            title="Sign out"
          >
            <LogoutIcon />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
